import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        /* keep trying */
      }
      tries.current += 1;
      if (tries.current > 15) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-root" className="min-h-screen grid place-items-center p-6" style={{ background: "hsl(var(--navy))" }}>
      <div className="w-full max-w-md rounded-3xl bg-card p-8 text-center shadow-2xl">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin mx-auto" style={{ color: "hsl(var(--cyan))" }} />
            <div className="mt-5 font-heading text-2xl" style={{ color: "hsl(var(--navy))" }}>Confirming your payment…</div>
            <p className="text-sm text-muted-foreground mt-2">This takes just a moment. Please don&apos;t close this window.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full grid place-items-center mx-auto" style={{ background: "hsl(var(--cyan))" }}>
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <div className="mt-5 font-heading text-2xl" style={{ color: "hsl(var(--navy))" }}>Payment successful</div>
            <p className="text-sm text-muted-foreground mt-2">
              Your {info?.plan_name || "plan"} is now active{info?.valid_until ? ` until ${info.valid_until}` : ""}. A receipt has been emailed to you.
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/billing" data-testid="success-billing-btn" className="rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: "hsl(var(--navy))" }}>View billing</Link>
              <button data-testid="success-dashboard-btn" onClick={() => navigate("/")} className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold">Go to dashboard</button>
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full grid place-items-center mx-auto bg-red-100">
              <XCircle className="w-9 h-9 text-red-600" />
            </div>
            <div className="mt-5 font-heading text-2xl" style={{ color: "hsl(var(--navy))" }}>We couldn&apos;t confirm payment</div>
            <p className="text-sm text-muted-foreground mt-2">If money was deducted it will auto-refund. You can try again from the pricing page.</p>
            <Link to="/pricing" data-testid="failed-back-to-pricing" className="mt-6 inline-flex rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: "hsl(var(--navy))" }}>Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
